import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, Box } from '@mui/material';
import styled from '@emotion/styled';
import { faChartLine, faDoorOpen, faFileSignature, faPeopleArrows, faRoadBarrier, faSignInAlt } from '@fortawesome/free-solid-svg-icons';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import { KpiTile } from '../analytics/components/KpiTile';
import { FaIcon } from '../common/FaIcon';
import { QcsChip } from '../common/basic/QcsChip';
import { QcsPaper } from '../common/basic/QcsPaper';
import { QcsTypography } from '../common/basic/QcsTypography';

const TileGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))',
  gap: '1rem',
});

const ModuleGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(21rem, 1fr))',
  gap: '1rem',
});

const ModuleCard = styled(QcsPaper)<{ enabled: boolean }>(({ theme, enabled }) => ({
  padding: '1.25rem 1.5rem',
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  backgroundColor: enabled ? theme.palette.background.paper : theme.palette.grey[100],
}));

const ModuleHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
});

const ModuleIcon = styled.span<{ enabled: boolean }>(({ theme, enabled }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '2.25rem',
  height: '2.25rem',
  borderRadius: '10px',
  color: enabled ? theme.palette.primary.main : theme.palette.text.disabled,
  backgroundColor: enabled ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.text.disabled, 0.08),
}));

const ModuleTitle = styled.div(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  fontSize: '0.925rem',
  fontWeight: 600,
  lineHeight: 1.3,
  color: theme.palette.text.primary,
}));

const ModuleDescription = styled.div(({ theme }) => ({
  fontSize: '0.8rem',
  lineHeight: 1.5,
  color: theme.palette.text.secondary,
}));

const MODULES = [
  { key: 'passRisks', icon: faRoadBarrier },
  { key: 'workplaceHandover', icon: faFileSignature },
  { key: 'turnstiles', icon: faDoorOpen },
  { key: 'entryRequests', icon: faSignInAlt },
  { key: 'analytics', icon: faChartLine },
  { key: 'subcontractors', icon: faPeopleArrows },
] as const;

export const CompSettTabBusiness: FC = () => {
  const { t } = useTranslation();
  const businessSettings = useBusinessSettings();

  const moduleEnabled: Record<(typeof MODULES)[number]['key'], boolean | undefined> = {
    passRisks: businessSettings?.passRisksEnabled,
    workplaceHandover: businessSettings?.workplaceHandoverEnabled,
    turnstiles: businessSettings?.turnstilesEnabled,
    entryRequests: businessSettings?.allowEntryRequests,
    analytics: businessSettings?.allowAnalytics,
    subcontractors: businessSettings?.allowSubcontractors,
  };

  return (
    <Box mt={2}>
      <TileGrid>
        <KpiTile labelKey="businessSettings.maxEstablishments" value={businessSettings?.maxEstablishments ?? '—'} />
        <KpiTile labelKey="businessSettings.maxUsers" value={businessSettings?.maxUsers ?? '—'} />
        <KpiTile labelKey="businessSettings.maxTraining" value={businessSettings?.maxTraining ?? '—'} />
      </TileGrid>

      <QcsTypography variant="body2" sx={{ fontWeight: 600, fontSize: '1rem', mt: 3, mb: 1.5 }}>
        {t('businessSettings.modulesTitle')}
      </QcsTypography>

      <ModuleGrid>
        {MODULES.map(({ key, icon }) => {
          const enabled = !!moduleEnabled[key];

          return (
            <ModuleCard key={key} enabled={enabled}>
              <ModuleHeader>
                <ModuleIcon enabled={enabled}>
                  <FaIcon icon={icon} width={18} height={18} />
                </ModuleIcon>
                <ModuleTitle>{t(`businessSettings.modules.${key}.title`)}</ModuleTitle>
                <QcsChip
                  isBold
                  label={enabled ? t('businessSettings.enabled') : t('businessSettings.disabled')}
                  color={enabled ? 'success' : 'default'}
                  variant={enabled ? 'filled' : 'outlined'}
                  sx={{ flexShrink: 0 }}
                />
              </ModuleHeader>
              <ModuleDescription>{t(`businessSettings.modules.${key}.description`)}</ModuleDescription>
            </ModuleCard>
          );
        })}
      </ModuleGrid>
    </Box>
  );
};
